import {type CyberOwlTheme} from './theme'
import {Colors} from './colours'
import {ColorsV2} from './colors-v2'

export const LightTheme: CyberOwlTheme = {
    colors: {
        text: {
            default: Colors.grey5,
            deemphasize: Colors.grey2,
            error: Colors.red4,
            warning: Colors.red3,
            success: Colors.green2,
            secondary: Colors.grey3,
        },
        background: {
            selectedLocationPage: Colors.grey13,
            page: Colors.grey1,
            default: Colors.white,
            lowContrast: Colors.grey6,
            fade: Colors.grey15,
        },
        boxShadow: {
            highContrast: '#00000034',
        },
        link: {
            primary: Colors.blue2,
            disabled: Colors.grey3,
        },
        border: {
            lowContrast: Colors.grey2,
            active: Colors.blue2,
            default: Colors.grey3,
        },
        newIndicator: {
            text: Colors.red3,
            flash: Colors.red4,
            background: Colors.red5,
        },
        graph: {
            primary: Colors.grey5,
            secondary: Colors.blue9,
        },
    },
    login: {
        errorBorder: Colors.red3,
        errorBackground: Colors.red3_21percent,
        errorContent: Colors.white,
    },
    loadingPage: {
        background: Colors.grey1,
    },
    taskStatus: {
        assigned: {
            background: ColorsV2.sky[100],
            text: ColorsV2.gray[700],
        },
        unassigned: {
            background: ColorsV2.gray[200],
            text: ColorsV2.gray[700],
        },
        rejected: {
            background: ColorsV2.red[200],
            text: ColorsV2.gray[700],
        },
        done: {
            background: ColorsV2.green[200],
            text: ColorsV2.gray[700],
        },
    },
    threatScores: {
        title: {
            unknown: {
                background: Colors.white,
                text: Colors.white,
            },
            low: {
                background: Colors.purple1,
                text: Colors.purple6,
            },
            medium: {
                background: Colors.purple5,
                text: Colors.white,
            },
            high: {
                background: Colors.purple6,
                text: Colors.white,
            },
        },
        score: {
            unknown: {
                background: Colors.white,
                text: Colors.grey5,
            },
            lowScoreLowValue: {
                background: Colors.purple1,
                text: Colors.purple6,
            },
            lowScoreMediumValue: {
                background: Colors.purple5,
                text: Colors.white,
            },
            lowScoreHighValue: {
                background: Colors.purple6,
                text: Colors.white,
            },
            low: {
                background: Colors.blue3,
                text: Colors.white,
            },
            medium: {
                background: Colors.orange2,
                background30p: Colors.orange2_30percent,
                text: Colors.grey5,
            },
            high: {
                background: Colors.red3,
                background30p: Colors.red3_30percent,
                text: Colors.white,
            },
        },
    },
    modalDialog: {
        background: Colors.white,
        text: Colors.grey5,
        overlay: 'rgba(0, 0, 0, 0.25)',
        divider: Colors.grey2,
        dropShadow: Colors.grey4,
        dataArea: {
            background: Colors.grey6,
        },
        dataRow: {
            text: Colors.grey5,
            background: Colors.white,
            border: '#000000ff',
        },
        tabs: {
            selected: {
                text: Colors.blue2,
                background: Colors.white,
            },
            notSelected: {
                text: Colors.grey5,
                background: Colors.grey6,
            },
        },
    },
    incidents: {
        dataArea: {
            background: Colors.grey6,
            historyBackground: Colors.grey1,
        },
        dataRow: {
            text: Colors.grey5,
            editText: 'rgba(133, 133, 133, 1)',
            background: Colors.white,
            newBackground: Colors.red1,
            separator: Colors.grey1,
            statusBar: {
                new: Colors.red3,
                open: Colors.orange2,
                closed: Colors.green1,
                draft: Colors.grey2,
            },
            selectedRow: Colors.grey2,
            saveNoteBackground: Colors.orange4,
            hoverNoteBackground: 'rgba(196, 196, 196, 0.22)',
            editNoteBackground: 'rgba(31, 136, 229, 0.1)',
            errorModal: Colors.grey14,
        },
        incidentStatus: {
            new: Colors.red3,
            open: Colors.orange2,
            closed: Colors.green1,
            draft: Colors.grey2,
        },
        incidentStatusPopup: {
            background: Colors.white,
            border: Colors.grey2,
        },
        incidentSeverity: {
            active: {
                low: Colors.blue3,
                medium: Colors.orange2,
                high: Colors.red3,
                critical: '#000C1E',
            },
            opacity: {
                low: Colors.blue8,
                medium: Colors.orange5,
                high: Colors.red8,
                critical: 'rgba(204,206,210,1)',
            },
        },
        resetButton: {
            text: Colors.white,
            background: Colors.blue2,
        },
        incidentAsset: {
            text: Colors.white,
            // CDS-6008: neutral fill now the SFM threat shading is gone.
            background: Colors.grey4,
        },
        newRecordLabel: {
            background: 'rgba(243, 70, 99, 0.5)',
            text: Colors.grey5,
        },
        filterBar: {
            background: Colors.white,
            boxShadow: 'rgba(0, 0, 0, 0.16)',
            titleSeparator: Colors.grey2,
            headingHighlight: Colors.grey5,
            search: {
                text: Colors.grey5,
                border: Colors.grey2,
            },
            sortOrder: {
                text: Colors.grey5,
                border: Colors.grey2,
                arrow: {icon: Colors.white, background: Colors.blue2},
                direction: Colors.blue2,
            },
            closeButton: {
                text: Colors.white,
                background: Colors.blue2,
                border: Colors.blue2,
            },
            savedFilters: {
                detailBackground: Colors.blue8,
            },
        },
    },
    systemPage: {
        dataArea: {
            background: Colors.grey6,
        },
        dataRow: {
            text: Colors.grey5,
            background: Colors.white,
            dropShadow: 'rgba(0, 0, 0, 0.3)',
        },
        link: {
            primary: Colors.blue2,
            hoverBackground: Colors.blue6,
        },
    },
    vessels: {
        dataArea: {
            background: Colors.grey6,
        },
        dataRow: {
            text: Colors.grey5,
            background: Colors.white,
            dropShadow: 'rgba(0, 0, 0, 0.3)',
        },
        link: {
            primary: Colors.blue2,
            hoverBackground: Colors.blue6,
        },
        modal: {
            boxShadow: 'rgba(0, 0, 0, 0.5)',
            hoverBackgroundColor: Colors.blue2,
            hoverTextColor: Colors.white,
        },
        button: {
            hoverBorderColor: 'transparent',
            hoverBackgroundColor: Colors.blue2,
            hoverTextColor: Colors.white,
        },
        list: {hoverBackgroundColor: Colors.blue2, hoverTextColor: Colors.white},
    },
    navigationResponsive: {
        dataArea: {
            background: Colors.grey7,
            miniBackground: Colors.grey5,
            locationExpandedArea: Colors.grey4,
            textColor: Colors.grey1,
            miniTextColor: Colors.grey11,
        },
        active: {
            background: Colors.red3,
            underLine: Colors.orange2,
        },
    },
    unknownAssets: {
        dataArea: {
            background: Colors.grey6,
        },
        dataRow: {
            text: Colors.grey5,
            background: Colors.white,
            selectedRow: Colors.grey2,
        },
        resetButton: {
            text: Colors.white,
            background: Colors.blue2,
        },
        filterBar: {
            boxShadow: 'rgba(0, 0, 0, 0.16)',
            closeButton: {
                text: Colors.white,
                background: Colors.blue2,
                border: Colors.blue2,
            },
            titleSeparator: Colors.grey2,
        },
    },
    userManagement: {
        dataArea: {
            background: Colors.grey6,
        },
        dataRow: {
            text: Colors.grey5,
            background: Colors.white,
            selectedRow: Colors.grey2,
            dropShadow: 'rgba(0, 0, 0, 0.3)',
        },
        resetButton: {
            text: Colors.white,
            background: Colors.blue2,
        },
    },
    vesselFilter: {
        filterBar: {
            background: Colors.white,
            boxShadow: 'rgba(0, 0, 0, 0.16)',
            titleSeparator: Colors.grey2,
            headingHighlight: Colors.grey5,
            search: {
                text: Colors.grey5,
                border: Colors.grey2,
            },
            sortOrder: {
                text: Colors.grey5,
                border: Colors.grey2,
                arrow: {icon: Colors.white, background: Colors.blue2},
                direction: Colors.blue2,
            },
            closeButton: {
                text: Colors.white,
                background: Colors.blue2,
                border: Colors.blue2,
            },
        },
        dataRow: {
            text: Colors.grey5,
            background: Colors.white,
            separator: Colors.grey1,
        },
    },
    myVessels: {
        dataArea: {
            background: Colors.grey1,
        },
        actionButton: {
            text: Colors.blue2,
            background: Colors.white,
            border: Colors.blue2,
        },
        resetButton: {
            text: Colors.white,
            background: Colors.blue2,
        },
        zoomLevel: {
            border: Colors.grey2,
            selected: {
                text: Colors.white,
                background: Colors.blue2,
            },
            unselected: {
                hoverBackground: Colors.blue1,
                background: Colors.white,
            },
        },
        filterBar: {
            background: Colors.white,
            boxShadow: 'rgba(0, 0, 0, 0.16)',
            titleSeparator: Colors.grey2,
            headingHighlight: Colors.grey5,
            search: {
                text: Colors.grey5,
                border: Colors.grey2,
            },
            sortOrder: {
                text: Colors.grey5,
                border: Colors.grey2,
                arrow: {icon: Colors.white, background: Colors.blue2},
                direction: Colors.blue2,
            },
            closeButton: {
                text: Colors.white,
                background: Colors.blue2,
                border: Colors.blue2,
            },
        },
        cards: {
            background: Colors.white,
            lowRiskText: Colors.grey7,
            lowRiskBackground: Colors.white,
            mediumRiskText: Colors.grey7,
            mediumRiskBackground: 'rgb(253,237,214)',
            highRiskText: Colors.grey7,
            highRiskBackground: 'rgb(252,213,219)',
            boxShadow: 'rgba(0, 0, 0, 0.16)',
        },
    },
    reports: {
        dataArea: {
            background: Colors.grey6,
            boxShadow: 'rgba(0, 0, 0, 0.3)',
        },
        resetButton: {
            text: Colors.white,
            background: Colors.blue2,
        },
        dataRow: {
            text: Colors.grey5,
            background: Colors.white,
            separator: Colors.grey1,
        },
        starRating: {
            selected: {
                fill: Colors.white,
                background: Colors.blue2,
            },
            unSelected: {
                fill: Colors.grey5,
                background: Colors.white,
            },
            hover: {
                fill: Colors.blue2,
                background: Colors.blue6,
            },
            staticIcon: {
                selected: Colors.grey5,
                unSelected: Colors.grey2,
            },
        },
        actionButton: {
            text: Colors.blue2,
            background: Colors.white,
            border: Colors.blue2,
        },
        filterBar: {
            boxShadow: 'rgba(0, 0, 0, 0.16)',
            closeButton: {
                text: Colors.white,
                background: Colors.blue2,
                border: Colors.blue2,
            },
        },
    },
    usbDevices: {
        dataArea: {
            background: Colors.grey6,
        },
        dataRow: {
            text: Colors.grey5,
            background: Colors.white,
        },
        resetButton: {
            text: Colors.white,
            background: Colors.blue2,
        },
        filterBar: {
            boxShadow: 'rgba(0, 0, 0, 0.16)',
            closeButton: {
                text: Colors.white,
                background: Colors.blue2,
                border: Colors.blue2,
            },
        },
    },
    softwareInventory: {
        dataArea: {
            background: Colors.grey6,
        },
        dataRow: {
            text: Colors.grey5,
            background: Colors.white,
            selectedRow: Colors.grey2,
        },
        resetButton: {
            text: Colors.white,
            background: Colors.blue2,
        },
        filterBar: {
            boxShadow: 'rgba(0, 0, 0, 0.16)',
            closeButton: {
                text: Colors.white,
                background: Colors.blue2,
                border: Colors.blue2,
            },
            titleSeparator: Colors.grey2,
        },
    },
    pagingButton: {
        clickable: {
            default: {
                background: 'inherit',
                text: Colors.grey5,
                border: Colors.grey5,
            },
            hover: {
                background: Colors.blue6,
                text: Colors.blue2,
                border: Colors.blue2,
            },
            active: {
                background: Colors.blue4,
                text: Colors.white,
                border: Colors.blue5,
            },
        },
        disabled: {
            background: 'inherit',
            text: Colors.grey2,
            border: Colors.grey2,
        },
        selected: {
            background: Colors.blue2,
            text: Colors.white,
        },
    },
    refreshButton: {
        default: {
            borderColor: 'transparent',
            backgroundColor: 'default',
            textColor: Colors.blue2,
        },
        hover: {
            borderColor: 'transparent',
            backgroundColor: Colors.blue2,
            textColor: Colors.white,
        },
        active: {
            borderColor: Colors.blue5,
            backgroundColor: Colors.blue2,
            textColor: Colors.white,
        },
        disabled: {
            borderColor: '#transparent',
            backgroundColor: 'default',
            textColor: Colors.white,
        },
    },
    dashboard: {
        noData: {
            text: Colors.grey2,
        },
        graphButtons: {
            active: {
                text: Colors.grey5,
                background: 'inherit',
                indicator: Colors.blue2,
            },
            selectable: {
                text: Colors.blue2,
                background: 'inherit',
            },
            hover: {
                text: Colors.blue2,
                background: Colors.blue7,
            },
        },
        fleetRiskScore: {
            low: {
                color: Colors.blue3,
                opacity: 1,
            },
            medium: {
                color: Colors.orange2,
                opacity: 0.3,
            },
            high: {
                color: Colors.red3,
                opacity: 0.3,
            },
        },
        vesselList: {
            backgroundColor: Colors.grey9,
            vesselDetail: {
                default: {backgroundColor: Colors.white},
                expanded: {backgroundColor: Colors.grey10},
            },
            assetDetail: {
                default: {backgroundColor: Colors.white},
                expanded: {backgroundColor: Colors.grey10},
            },
        },
    },
    commonTableViewPage: {
        dataArea: {
            background: Colors.grey6,
        },
        dataRow: {
            text: Colors.grey5,
            background: Colors.white,
            selectedRow: Colors.grey2,
            boxShadow: 'rgba(0, 0, 0, 0.3)',
        },
    },
    font: {
        family: "'Open Sans', sans-serif",
        weight: {
            light: 300,
            normal: 400,
            semibold: 600,
            bold: 700,
            extrabold: 800,
        },
    },
    vesselScore: {
        low: Colors.green3,
        medium: Colors.orange6,
        high: Colors.darkRed,
    },
}
